'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

export type AuthMode = 'login' | 'signup';

/**
 * Étapes possibles.
 * - Connexion : 'password' → (optionnel) 'forgotIdentifier' → 'forgotChannel' → 'forgotCode'
 * - Création : 'register' → 'channel' → 'code'
 *
 * 2026-08-13 — le code SMS comme MÉTHODE de connexion/création est retiré :
 * la création de compte passe désormais toujours par le formulaire mot de
 * passe (nom d'utilisateur, email, téléphone, mot de passe), et la
 * connexion exige toujours identifiant + mot de passe. Le SMS reste utilisé,
 * mais seulement comme CANAL de livraison d'un code de vérification — au
 * choix, à la création du compte et lors d'un mot de passe oublié — jamais
 * comme méthode de connexion à part entière. `/api/auth/phone/request-code`
 * + `/api/auth/phone/verify-code` restent en place côté serveur (inchangés)
 * mais ne sont plus appelés depuis cet écran.
 *
 * 2026-08-16 — le choix SMS est masqué à son tour, côté écran seulement : le
 * canal SMS lui-même (TPEcloud) n'est pas forcément en cause — c'est que le
 * drainage des files (email ET message) dépend de 9 tâches planifiées qui ne
 * s'exécutent pas toutes seules hors Vercel (voir /diagnostic et
 * docs/mise-en-ligne.md, « 6. Tâches planifiées »). Tant que ce n'est pas
 * reconfirmé actif en production, un choix « Par SMS » que quelqu'un clique
 * en confiance peut rester silencieusement sans réponse. `chooseSignupChannel`
 * / `chooseForgotChannel` sont donc appelées directement avec 'email', sans
 * jamais passer par `ChannelPicker` — le composant et les steps 'channel' /
 * 'forgotChannel' restent en place, prêts à être rebranchés (il suffit de
 * remettre `setStep('channel')` / `setStep('forgotChannel')` à la place des
 * appels directs ci-dessous) une fois le canal SMS confirmé de bout en bout.
 */
type Step =
  | 'password'
  | 'register'
  | 'channel'
  | 'code'
  | 'forgotIdentifier'
  | 'forgotChannel'
  | 'forgotCode';

type Channel = 'sms' | 'email';

/**
 * Deux boutons « SMS » / « Email » — utilisé à la fois pour choisir le canal
 * de vérification à la création du compte et pour le mot de passe oublié.
 */
function ChannelPicker({
  pending,
  onPick,
}: {
  pending: boolean;
  onPick: (channel: Channel) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        disabled={pending}
        onClick={() => onPick('sms')}
        className="flex flex-col items-center gap-2 rounded-btn border-[1.5px] border-line bg-white px-[18px] py-5 font-sans text-[15px] font-semibold text-navy disabled:opacity-60"
      >
        <Icon name="phone" size={20} />
        Par SMS
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={() => onPick('email')}
        className="flex flex-col items-center gap-2 rounded-btn border-[1.5px] border-line bg-white px-[18px] py-5 font-sans text-[15px] font-semibold text-navy disabled:opacity-60"
      >
        <Icon name="lock" size={20} />
        Par email
      </button>
    </div>
  );
}

function ErrorBanner({ error }: { error: string }) {
  return (
    <p className="mb-4 rounded-[12px] bg-red-bg px-4 py-2.5 text-[13.5px] font-medium text-red">
      {error}
    </p>
  );
}

export function AuthPanel({
  initialMode,
  next,
}: {
  initialMode: AuthMode;
  /** Chemin interne où revenir après connexion — une invitation, en pratique. */
  next?: string;
}) {
  const router = useRouter();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [step, setStep] = useState<Step>(initialMode === 'login' ? 'password' : 'register');
  const [channel, setChannel] = useState<Channel | null>(null);

  // Connexion par mot de passe.
  const [identifier, setIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Création par mot de passe.
  const [regUsername, setRegUsername] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [signupCode, setSignupCode] = useState('');
  const [resendIn, setResendIn] = useState(0);

  // Mot de passe oublié.
  const [forgotCode, setForgotCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  /** Le backend renvoie déjà un message en français : on l'affiche tel quel. */
  const readError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      const message = err.body['message'];
      if (typeof message === 'string' && message) {
        const left = err.body['attemptsLeft'];
        return typeof left === 'number' && left > 0
          ? `${message} Il vous reste ${left} tentative${left > 1 ? 's' : ''}.`
          : message;
      }
      // Zod renvoie `issues` sans `message` de haut niveau (VALIDATION_FAILED,
      // ex. mot de passe/confirmation différents) — prendre le premier.
      const issues = err.body['issues'];
      if (Array.isArray(issues) && issues.length > 0) {
        const first = issues[0] as { message?: unknown } | undefined;
        if (typeof first?.message === 'string' && first.message) return first.message;
      }
    }
    return fallback;
  };

  const resetToDefault = useCallback((nextMode: AuthMode) => {
    setMode(nextMode);
    setStep(nextMode === 'login' ? 'password' : 'register');
    setChannel(null);
    setError(null);
    setNotice(null);
    setSignupCode('');
    setForgotCode('');
    setNewPassword('');
    setNewPasswordConfirm('');
  }, []);

  /** Après une session ouverte, où l'envoyer. */
  const goPostAuth = useCallback(async () => {
    // Une invitation en cours l'emporte : elle est venue pour rejoindre une
    // tontine, pas pour en créer une.
    if (next) {
      router.push(next);
      return;
    }
    // Une gérante sans tontine part configurer la sienne ; les autres vont
    // directement à leur tableau de bord.
    const { tontines } = await api<{ tontines: unknown[] }>('/api/tontines');
    router.push(tontines.length === 0 ? '/onboarding' : '/tableau-de-bord');
  }, [next, router]);

  const submitPasswordLogin = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      await api('/api/auth/login', {
        method: 'POST',
        body: { identifier, password: loginPassword },
      });
      await goPostAuth();
    } catch (err) {
      setError(readError(err, 'Identifiants incorrects.'));
      setPending(false);
    }
  }, [identifier, loginPassword, goPostAuth]);

  const chooseSignupChannel = useCallback(
    async (picked: Channel) => {
      setPending(true);
      setError(null);
      try {
        await api('/api/auth/register', {
          method: 'POST',
          body: {
            username: regUsername,
            email: regEmail,
            phone: regPhone,
            password: regPassword,
            confirmPassword: regConfirm,
            channel: picked,
          },
        });
        // Même réponse que le compte existe déjà ou non (anti-énumération
        // côté serveur) — on avance toujours vers l'étape de vérification ;
        // si un email/téléphone était déjà pris, aucun code n'arrivera et la
        // femme devra se connecter normalement à la place.
        setChannel(picked);
        setResendIn(60);
        setStep('code');
      } catch (err) {
        setError(readError(err, 'Impossible de créer le compte. Réessayez dans un instant.'));
      } finally {
        setPending(false);
      }
    },
    [regUsername, regEmail, regPhone, regPassword, regConfirm],
  );

  /**
   * Étape purement locale : valide le formulaire puis enchaîne directement
   * sur /api/auth/register. Auparavant un choix SMS/Email s'intercalait ici
   * (`setStep('channel')`) — voir la note du 2026-08-16 en tête de fichier :
   * le canal est forcé à 'email' le temps de reconfirmer les tâches
   * planifiées en production.
   */
  const submitRegisterForm = useCallback(() => {
    setError(null);
    if (regPassword !== regConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    void chooseSignupChannel('email');
  }, [regPassword, regConfirm, chooseSignupChannel]);

  const resendSignupCode = useCallback(async () => {
    if (!channel) return;
    setPending(true);
    setError(null);
    try {
      await api('/api/auth/resend-verification', {
        method: 'POST',
        body: { identifier: channel === 'email' ? regEmail : regPhone, channel },
      });
      setResendIn(60);
    } catch (err) {
      setError(readError(err, 'Impossible de renvoyer le code.'));
    } finally {
      setPending(false);
    }
  }, [channel, regEmail, regPhone]);

  const submitSignupCode = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      await api('/api/auth/verify-email', {
        method: 'POST',
        body:
          channel === 'sms'
            ? { phone: regPhone, code: signupCode }
            : { email: regEmail, code: signupCode },
      });
      // Jamais de connexion automatique après une création de compte : elle
      // prouve son mot de passe une première fois, comme tout le monde.
      setIdentifier(regUsername);
      setLoginPassword('');
      setNotice('Compte créé. Connectez-vous avec votre mot de passe.');
      setMode('login');
      setStep('password');
      setSignupCode('');
    } catch (err) {
      setError(readError(err, 'Code incorrect.'));
      setPending(false);
      return;
    }
    setPending(false);
  }, [channel, regPhone, regEmail, signupCode, regUsername]);

  const chooseForgotChannel = useCallback(
    async (picked: Channel) => {
      setPending(true);
      setError(null);
      try {
        await api('/api/auth/forgot-password', {
          method: 'POST',
          body: { identifier, channel: picked },
        });
        setChannel(picked);
        setResendIn(60);
        setStep('forgotCode');
      } catch (err) {
        setError(readError(err, 'Impossible d’envoyer le code. Réessayez dans un instant.'));
      } finally {
        setPending(false);
      }
    },
    [identifier],
  );

  /**
   * Auparavant un choix SMS/Email s'intercalait ici (`setStep('forgotChannel')`)
   * — voir la note du 2026-08-16 en tête de fichier : le canal est forcé à
   * 'email' le temps de reconfirmer les tâches planifiées en production.
   */
  const submitForgotIdentifier = useCallback(() => {
    setError(null);
    if (!identifier.trim()) return;
    void chooseForgotChannel('email');
  }, [identifier, chooseForgotChannel]);

  const submitForgotReset = useCallback(async () => {
    setError(null);
    if (newPassword !== newPasswordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setPending(true);
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: { identifier, code: forgotCode, newPassword },
      });
      setLoginPassword('');
      setNotice('Mot de passe mis à jour. Connectez-vous.');
      setStep('password');
      setForgotCode('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err) {
      setError(readError(err, 'Code incorrect.'));
    } finally {
      setPending(false);
    }
  }, [identifier, forgotCode, newPassword, newPasswordConfirm]);

  const login = mode === 'login';
  const showGoogle = step === 'password' || step === 'register';

  const heading =
    step === 'code' || step === 'forgotCode'
      ? 'Entrez votre code'
      : step === 'channel' || step === 'forgotChannel'
        ? 'Comment recevoir le code ?'
        : step === 'forgotIdentifier'
          ? 'Mot de passe oublié'
          : login
            ? 'Bon retour parmi nous'
            : 'Créez votre tontine';

  const subtitle =
    step === 'code'
      ? `Nous avons envoyé un code à ${channel === 'sms' ? regPhone : regEmail}.`
      : step === 'forgotCode'
        ? `Nous avons envoyé un code par ${channel === 'sms' ? 'SMS' : 'email'} si ce compte existe.`
        : step === 'channel' || step === 'forgotChannel'
          ? 'Un code de vérification vous sera envoyé.'
          : step === 'forgotIdentifier'
            ? 'Entrez votre identifiant, nous vous enverrons un code.'
            : login
              ? 'Entrez votre identifiant et votre mot de passe.'
              : '5 minutes suffisent. Gratuit jusqu’à 15 membres.';

  return (
    <div className="flex items-center justify-center bg-white px-7 py-10">
      <div className="w-full max-w-[400px] animate-tmfade">
        <div className="mb-7 flex rounded-btn bg-cream p-1">
          {(['login', 'signup'] as AuthMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => resetToDefault(m)}
              className={cn(
                'flex-1 cursor-pointer rounded-[9px] p-2.5 font-sans text-[14.5px] font-semibold',
                mode === m
                  ? 'bg-white text-navy shadow-[0_2px_6px_rgba(0,0,0,.06)]'
                  : 'bg-transparent text-mut',
              )}
            >
              {m === 'login' ? 'Connexion' : 'Créer un compte'}
            </button>
          ))}
        </div>

        <h1 className="m-0 mb-1.5 font-display text-[27px] font-extrabold tracking-[-0.5px] text-navy">
          {heading}
        </h1>
        <p className="m-0 mb-[26px] text-[14.5px] text-mut">{subtitle}</p>

        {notice ? (
          <p className="mb-4 rounded-[12px] bg-green-bg px-4 py-2.5 text-[13.5px] font-medium text-green-d">
            {notice}
          </p>
        ) : null}

        {step === 'password' && login ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitPasswordLogin();
            }}
          >
            <Field
              label="Nom d'utilisateur, email ou téléphone"
              placeholder="awa_diallo"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Mot de passe"
              type="password"
              icon="lock"
              autoComplete="current-password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              {...(error ? { error } : {})}
            />
            <div className="mt-3 text-right">
              <button
                type="button"
                className="cursor-pointer text-[13.5px] text-mut underline"
                onClick={() => {
                  setError(null);
                  setNotice(null);
                  setStep('forgotIdentifier');
                }}
              >
                Mot de passe oublié ?
              </button>
            </div>
            <Button
              type="submit"
              icon="arrow"
              className="mt-4 w-full"
              disabled={pending || !identifier || !loginPassword}
            >
              {pending ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>
        ) : null}

        {step === 'register' && !login ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitRegisterForm();
            }}
          >
            <Field
              label="Nom d'utilisateur"
              placeholder="awa_diallo"
              autoComplete="username"
              value={regUsername}
              onChange={(e) => setRegUsername(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Numéro de téléphone"
              placeholder="07 08 12 34 56"
              icon="phone"
              inputMode="tel"
              autoComplete="tel"
              value={regPhone}
              onChange={(e) => setRegPhone(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Mot de passe"
              type="password"
              icon="lock"
              autoComplete="new-password"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Confirmez le mot de passe"
              type="password"
              icon="lock"
              autoComplete="new-password"
              value={regConfirm}
              onChange={(e) => setRegConfirm(e.target.value)}
              {...(error ? { error } : {})}
            />
            <Button
              type="submit"
              icon="arrow"
              className="mt-6 w-full"
              disabled={
                pending || !regUsername || !regEmail || !regPhone || !regPassword || !regConfirm
              }
            >
              {pending ? 'Création…' : 'Continuer'}
            </Button>
          </form>
        ) : null}

        {step === 'channel' ? (
          <div>
            {error ? <ErrorBanner error={error} /> : null}
            <ChannelPicker pending={pending} onPick={(c) => void chooseSignupChannel(c)} />
            <div className="mt-4 text-center">
              <button
                type="button"
                className="cursor-pointer text-[13.5px] text-mut underline"
                onClick={() => {
                  setError(null);
                  setStep('register');
                }}
              >
                Modifier mes informations
              </button>
            </div>
          </div>
        ) : null}

        {step === 'code' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitSignupCode();
            }}
          >
            <Field
              label="Code reçu"
              placeholder="AB3D-7F2K"
              icon="lock"
              autoComplete="one-time-code"
              value={signupCode}
              onChange={(e) => setSignupCode(e.target.value)}
              {...(error ? { error } : {})}
            />
            <Button
              type="submit"
              icon="arrow"
              className="mt-6 w-full"
              disabled={pending || !signupCode}
            >
              {pending ? 'Vérification…' : 'Valider'}
            </Button>
            <div className="mt-4 text-center text-[13.5px]">
              <button
                type="button"
                className="cursor-pointer text-mut underline disabled:no-underline disabled:opacity-60"
                disabled={pending || resendIn > 0}
                onClick={() => void resendSignupCode()}
              >
                {resendIn > 0 ? `Renvoyer le code (${resendIn} s)` : 'Renvoyer le code'}
              </button>
            </div>
          </form>
        ) : null}

        {step === 'forgotIdentifier' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForgotIdentifier();
            }}
          >
            <Field
              label="Nom d'utilisateur, email ou téléphone"
              placeholder="awa_diallo"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              {...(error ? { error } : {})}
            />
            <Button
              type="submit"
              icon="arrow"
              className="mt-6 w-full"
              disabled={pending || !identifier}
            >
              {pending ? 'Envoi…' : 'Continuer'}
            </Button>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="cursor-pointer text-[13.5px] text-mut underline"
                onClick={() => {
                  setError(null);
                  setStep('password');
                }}
              >
                Retour à la connexion
              </button>
            </div>
          </form>
        ) : null}

        {step === 'forgotChannel' ? (
          <div>
            {error ? <ErrorBanner error={error} /> : null}
            <ChannelPicker pending={pending} onPick={(c) => void chooseForgotChannel(c)} />
          </div>
        ) : null}

        {step === 'forgotCode' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitForgotReset();
            }}
          >
            <Field
              label="Code reçu"
              placeholder="AB3D-7F2K"
              icon="lock"
              autoComplete="one-time-code"
              value={forgotCode}
              onChange={(e) => setForgotCode(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Nouveau mot de passe"
              type="password"
              icon="lock"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mb-4"
            />
            <Field
              label="Confirmez le mot de passe"
              type="password"
              icon="lock"
              autoComplete="new-password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              {...(error ? { error } : {})}
            />
            <Button
              type="submit"
              icon="arrow"
              className="mt-6 w-full"
              disabled={pending || !forgotCode || !newPassword || !newPasswordConfirm}
            >
              {pending ? 'Mise à jour…' : 'Redéfinir mon mot de passe'}
            </Button>
            <div className="mt-4 text-center text-[13.5px]">
              <button
                type="button"
                className="cursor-pointer text-mut underline disabled:no-underline disabled:opacity-60"
                disabled={pending || resendIn > 0}
                onClick={() => channel && void chooseForgotChannel(channel)}
              >
                {resendIn > 0 ? `Renvoyer le code (${resendIn} s)` : 'Renvoyer le code'}
              </button>
            </div>
          </form>
        ) : null}

        {showGoogle ? (
          <>
            <div className="my-6 flex items-center gap-3 text-[13px] text-mut">
              <span className="h-px flex-1 bg-line" />
              ou
              <span className="h-px flex-1 bg-line" />
            </div>

            {/*
              Lien plein et non `router.push` : le démarrage OAuth est une
              redirection serveur qui pose les cookies state + PKCE, elle
              doit sortir du routeur client.

              `?next=` est indispensable : sans lui, `google/start` ne pose
              pas le cookie `app-oauth-next`, et `google/callback` retombe
              sur `APP_URL` (la page d'accueil publique) après une connexion
              pourtant réussie. `/tableau-de-bord` gère déjà le cas « aucune
              tontine » en proposant `/onboarding`, donc il reste sûr même
              pour un tout nouveau compte Google (qui, lui, passe d'abord par
              /auth/finaliser côté serveur — voir google/callback/route.ts).
            */}
            <a
              href={`/api/auth/oauth/google/start?next=${encodeURIComponent(next ?? '/tableau-de-bord')}`}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-btn border-[1.5px] border-line bg-white px-[22px] py-[13px] font-sans text-[15px] font-semibold text-navy"
            >
              <Icon name="users" size={18} />
              Continuer avec Google
            </a>
          </>
        ) : null}

        <div className="mt-[22px] text-center">
          <Link href="/" className="text-[13.5px] text-mut">
            ← Retour à l’accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
